<template>
  <UserModal
    v-if="userModalVisible"
    :mode="modalMode"
    :user="modalUser"
    @close-modal="userModalClose"
  />
  <UserDeleteModal
    v-if="deleteModalVisible"
    :user="deleteModalUser"
    @close-modal="deleteModalClose"
  />
  <div class="user-table">
    <div class="grid-control__container">
      <p class="section__title--text">Users</p>
      <div class="pull-right">
        <ClearFiltersButton @clearFiltersClicked="clearFilters" />
        <Button
          v-if="addNewUserVisible"
          type="button"
          theme-color="tertiary"
          @click="onAddClicked"
          >Add New User</Button
        >
      </div>
    </div>
    <UserGrid
      :filter="filter"
      :users="users"
      :loading="usersLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalUsers"
      :sort="sortDescriptors"
      :getPropValues="getPropValuesAsync"
      @editClicked="onEditClicked"
      @deleteClicked="onDeleteClicked"
      @sortChanged="onSortChanged"
      @usersChanged="onUsersChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref } from "vue";
import type { FindUsersByCompidWithFilteringRequest } from "@/api/userLogin";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import UserGrid from "./UserGrid.vue";
import type { MemberShipUserPlus } from "@/Domain/MemberShipUserPlus";
import UserDeleteModal from "./UserDeleteModal.vue";
import UserModal from "./UserModal.vue";
import { UserModalMode } from "@/Domain/UserModalMode";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import {
  boolYNConvert,
  boolYNConvertBackToString,
  FilterValuesTake,
  getJsonFilterString,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import { UserRoles } from "@/Domain/UserRoles";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();

const users = ref<MemberShipUserPlus[]>([]);
const totalUsers = ref<number>(0);
const usersLoading = ref<boolean>(false);
const skip = ref<number>(0);
const take = ref<number>(25);
const userModalVisible = ref<boolean>(false);
const deleteModalVisible = ref<boolean>(false);
const modalUser = ref<MemberShipUserPlus | null>(null);
const deleteModalUser = ref<MemberShipUserPlus>({} as MemberShipUserPlus);
const modalMode = ref<UserModalMode>(UserModalMode.Add);

const filter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});
const sortField = ref<string>("");
const sortAscending = ref<boolean>(true);

const tableName = "UserTable";
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});


const addNewUserVisible = computed(() => {
  return (
    main.isInRole(UserRoles.ROLE_EXTERNAL_CUSTOMER_ADMIN) ||
    main.isInRole(UserRoles.ROLE_IN_HOUSE_MIS_MANAGER)
  );
});

const valueFiltersConvertOptions = [
  {
    column: "AllPlants",
    convert: boolYNConvert,
    convertBack: boolYNConvertBackToString,
  },
] as ValueFieldConvertOption[];

const findUsersByCompidWithFilteringAsync = async function (
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    useCognito: true,
    filters: getJsonFilterString(filter.value, valueFiltersConvertOptions),
    take: take.value,
    skip: skip.value,
    valuesForFieldOnly: valuesForFieldOnly,
    sortField: sortField.value,
    ascending: sortAscending.value,
  } as FindUsersByCompidWithFilteringRequest;

  const endpoint = "FindUsersByCompidWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, endpoint),
    () => api.get(endpoint, requestParams)
  );
};

const findUsersAsync = async function () {
  if (usersLoading.value) return;
  usersLoading.value = true;

  await handleResponsePromiseAsync(
    findUsersByCompidWithFilteringAsync(),
    (data) => {
      users.value = data.ListItems;
      users.value.forEach((u) => {
        if (!u.PlantPermissions) u.PlantPermissions = [];
        if (!u.Roles) u.Roles = [];
      });
      totalUsers.value = data.TotalItemsInList;
      console.debug(`UserTable.vue: findUsersAsync(), result:`, data);
    },
    "UserTable.vue",
    "findUsersAsync"
  );

  usersLoading.value = false;
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: MemberShipUserPlus[] = [];
  await handleResponsePromiseAsync(
    findUsersByCompidWithFilteringAsync(column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "UserTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const onUsersChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    filter.value.filters = options.filterEvent.filter.filters;
  }
  if (take.value != options.take) {
    take.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  skip.value = options.skip;
  findUsersAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  findUsersAsync();
};

const onAddClicked = function () {
  modalUser.value = null;
  modalMode.value = UserModalMode.Add;
  userModalVisible.value = true;
};

const onEditClicked = function (user: MemberShipUserPlus) {
  modalUser.value = user;
  modalMode.value = UserModalMode.Edit;
  userModalVisible.value = true;
};

const onDeleteClicked = function (user: MemberShipUserPlus) {
  deleteModalUser.value = user;
  deleteModalVisible.value = true;
};

const userModalClose = function (userSaved: boolean) {
  userModalVisible.value = false;
  if (userSaved) findUsersAsync();
};

const deleteModalClose = function (userDeleted: boolean) {
  deleteModalVisible.value = false;
  if (userDeleted) findUsersAsync();
};

const clearFilters = function () {
  console.debug("UserTable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  sortAscending.value = false;
  sortField.value = "";
  findUsersAsync();
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  await findUsersAsync();
});
</script>

<style lang="scss">
.user-table {
  .grid-control__container {
    display: flex;
    align-items: center;
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;
  }
}
</style>
